/**
 * Injects annotation <span> tags into HTML content for word-level highlighting.
 *
 * Takes translated HTML and a list of annotations (from backend),
 * finds annotated text fragments in the HTML, and wraps them in spans
 * with data attributes for tooltip rendering.
 */

/**
 * Escape special regex characters in a string.
 */
function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Find and wrap annotation text in an HTML string, being careful not to
 * match inside HTML tags or attributes.
 *
 * @param {string} html - The HTML string for a single line
 * @param {string} text - The text to find and annotate
 * @param {number} annotationIdx - Index into the annotations array
 * @param {string} annotationType - The annotation type for data attribute
 * @returns {string} HTML with annotation spans injected
 */
function injectAnnotationSpan(html, text, annotationIdx, annotationType) {
  if (!text || !html) return html;

  // Build a regex that matches the text but NOT inside HTML tags
  // Strategy: split HTML into text nodes and tags, only match within text nodes
  const parts = html.split(/(<[^>]*>)/);
  let found = false;

  const result = parts.map(part => {
    // Skip HTML tags
    if (part.startsWith('<')) return part;
    // Skip if already found (only annotate first occurrence per line)
    if (found) return part;

    const pattern = new RegExp(`(${escapeRegex(text)})`, 'i');
    const match = part.match(pattern);

    if (match) {
      found = true;
      return part.replace(
        pattern,
        `<span class="annotation" data-annotation-idx="${annotationIdx}" data-type="${annotationType}">$1</span>`
      );
    }
    return part;
  });

  return result.join('');
}

/**
 * Annotate full HTML content with annotations grouped by line.
 *
 * The HTML content is the full translated document. Annotations reference
 * line indices from translated_lines. We need to match annotation text
 * within the corresponding HTML segments.
 *
 * @param {string} fullHtml - The full translated HTML content
 * @param {Object} translatedLines - Dict of line_index -> HTML string
 * @param {Array} annotations - List of annotation objects from backend
 * @returns {string} HTML with annotation spans injected
 */
export function annotateHtml(fullHtml, translatedLines, annotations) {
  if (!annotations || annotations.length === 0 || !fullHtml) {
    return fullHtml;
  }

  // If we don't have translatedLines, try to annotate directly in fullHtml
  if (!translatedLines || Object.keys(translatedLines).length === 0) {
    return annotateFullHtml(fullHtml, annotations);
  }

  // Group annotations by line_index
  const byLine = {};
  annotations.forEach((ann, idx) => {
    const lineIdx = ann.line_index;
    if (!byLine[lineIdx]) byLine[lineIdx] = [];
    byLine[lineIdx].push({ ...ann, _globalIdx: idx });
  });

  // For each line that has annotations, find its HTML in the full content
  // and inject spans. We replace line HTML in the full document.
  let annotatedHtml = fullHtml;

  for (const [lineIdx, lineAnnotations] of Object.entries(byLine)) {
    const lineHtml = translatedLines[lineIdx];
    if (!lineHtml) continue;

    // Sort annotations: longer text first (to avoid partial matches overriding full ones)
    const sorted = [...lineAnnotations].sort((a, b) => b.text.length - a.text.length);

    let annotatedLine = lineHtml;
    for (const ann of sorted) {
      annotatedLine = injectAnnotationSpan(
        annotatedLine,
        ann.text,
        ann._globalIdx,
        ann.type
      );
    }

    // Replace the original line HTML with annotated version in the full document
    if (annotatedLine !== lineHtml) {
      annotatedHtml = annotatedHtml.replace(lineHtml, annotatedLine);
    }
  }

  return annotatedHtml;
}

/**
 * Fallback: annotate directly in the full HTML when translatedLines is not available.
 */
function annotateFullHtml(fullHtml, annotations) {
  // Sort by text length descending to avoid partial match issues
  const sorted = [...annotations].map((ann, idx) => ({ ...ann, _globalIdx: idx }));
  sorted.sort((a, b) => b.text.length - a.text.length);

  let result = fullHtml;
  for (const ann of sorted) {
    result = injectAnnotationSpan(result, ann.text, ann._globalIdx, ann.type);
  }
  return result;
}
